import { defaultLocale, locales } from '@website/i18n/routing';

export const SITE_NAME = 'TopTopTime';

/** Re-declared by pages that set `openGraph`/`twitter`: Next replaces those objects instead of merging them. */
export const DEFAULT_OG_IMAGE = { url: '/og-default.png', width: 1200, height: 630, alt: SITE_NAME };

export type SiteLocale = (typeof locales)[number];

const OG_LOCALES: Record<SiteLocale, string> = { fr: 'fr_FR', en: 'en_US' };

/** Fields every page's `openGraph` must repeat, since Next replaces the root layout's object wholesale. */
export const openGraphBase = (locale: string) => ({
  siteName: SITE_NAME,
  locale: OG_LOCALES[locale as SiteLocale] ?? OG_LOCALES.fr
});

export const getSiteUrl = (): string =>
  (process.env.NEXT_PUBLIC_WEBSITE_URL || 'https://toptoptime.com').replace(/\/+$/, '');

export const absoluteUrl = (path: string): string => `${getSiteUrl()}${path.startsWith('/') ? path : `/${path}`}`;

export const isIndexableStage = (): boolean => process.env.NEXT_PUBLIC_SST_STAGE === 'production';

/** Locale-prefixed paths of one page, keyed by locale (slugs may differ per locale). */
export type LocalizedPaths = Record<SiteLocale, string>;

export const samePathForAllLocales = (path: string): LocalizedPaths =>
  Object.fromEntries(locales.map((l) => [l, `/${l}${path}`])) as LocalizedPaths;

/**
 * `alternates` for a page's metadata: a self-referencing canonical plus one
 * hreflang per locale. x-default points at the default-locale URL rather than
 * `/`, because `/` 307s on Accept-Language and crawlers need a stable target.
 */
export const buildAlternates = (paths: LocalizedPaths, locale: string) => {
  const current = paths[locale as SiteLocale] ?? paths[defaultLocale];
  return {
    canonical: absoluteUrl(current),
    languages: {
      ...(Object.fromEntries(locales.map((l) => [l, absoluteUrl(paths[l])])) as Record<SiteLocale, string>),
      'x-default': absoluteUrl(paths[defaultLocale])
    }
  };
};

/** Plain-text meta description, collapsed whitespace, cut on a word boundary. */
export const toMetaDescription = (text: string | undefined | null, max = 155): string => {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.–-]+$/, '')}…`;
};
